import React, { useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { userService } from "services/user/user.service"

import { formatFiatCurrency, parseIntSafe } from "util/formatter"

import { AffiliateRow } from "../UserAffiliateTab"

type Props = {
  open: boolean
  onClose: () => void
  userId: string
  affiliate: AffiliateRow
  onUpdated: () => void
}

export const ModalEditCpaConfig = ({ open, onClose, userId, affiliate, onUpdated }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const initial = affiliate.cpa_config ?? {}

  const [rewardCents, setRewardCents] = useState(String(initial.reward_cents ?? ""))
  const [requireKyc, setRequireKyc] = useState<"any" | "yes" | "no">(
    initial.require_kyc === true ? "yes" : initial.require_kyc === false ? "no" : "any"
  )
  const [minDepositCents, setMinDepositCents] = useState(String(initial.min_total_deposit_cents ?? ""))
  const [minWagerCents, setMinWagerCents] = useState(String(initial.min_total_wager_cents ?? ""))
  const [saving, setSaving] = useState(false)

  const rewardError = useMemo(() => {
    const n = Number(rewardCents)
    if (!rewardCents.trim()) return "reward_cents is required"
    if (!Number.isFinite(n) || n < 0) return "reward_cents must be >= 0"
    return null
  }, [rewardCents])

  const payload = useMemo(() => {
    const cfg: any = {
      reward_cents: Math.floor(Number(rewardCents || 0)),
      require_kyc: requireKyc === "any" ? null : requireKyc === "yes",
      min_total_deposit_cents: parseIntSafe(minDepositCents),
      min_total_wager_cents: parseIntSafe(minWagerCents)
    }
    return { cpa_config: cfg }
  }, [rewardCents, requireKyc, minDepositCents, minWagerCents])

  const canSave = open && !saving && !rewardError

  const save = async () => {
    if (rewardError) return toast.error(rewardError)

    try {
      setSaving(true)
      await userService.updateUserAffiliate(userId, affiliate.id, payload)
      toast.success("CPA config updated")
      onClose()
      onUpdated()
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to update CPA config")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className="w-[92vw] max-w-2xl rounded-2xl border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.10)] bg-white dark:bg-[#0C0C1A] shadow-xl">
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-lg font-semibold">{t("cpa.editTitle")}</div>
              <div className="mt-1 text-sm opacity-60">
                {t("cpa.affiliate")}: <span className="font-mono">{affiliate.code}</span>
              </div>
            </div>

            <button type="button" className={clsx("btn btn-ghost btn-sm", { "btn-disabled": saving })} onClick={onClose} disabled={saving}>
              ✕
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">
                {t("cpa.rewardCents")} {formatFiatCurrency(rewardCents)}
              </label>
              <input
                className={clsx("input input-bordered w-full", { "input-error": !!rewardError })}
                value={rewardCents}
                onChange={(e) => setRewardCents(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 500 (= $5.00)"
                disabled={saving}
              />
              {rewardError ? (
                <div className="text-xs text-error">{rewardError}</div>
              ) : (
                <div className="text-xs opacity-60">{t("cpa.storedInCents")}</div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">{t("cpa.requireKyc")}</label>
              <select
                className="select select-bordered w-full"
                value={requireKyc}
                onChange={(e) => setRequireKyc(e.target.value as any)}
                disabled={saving}
              >
                <option value="any">{t("cpa.any")}</option>
                <option value="yes">{t("common.yes")}</option>
                <option value="no">{t("common.no")}</option>
              </select>
              <div className="text-xs opacity-60">{t("cpa.eligibilityCondition")}</div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">
                {t("cpa.minTotalDeposit")} {formatFiatCurrency(minDepositCents)}
              </label>
              <input
                className="input input-bordered w-full"
                value={minDepositCents}
                onChange={(e) => setMinDepositCents(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 1000 (= $10.00)"
                disabled={saving}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm opacity-80">
                {t("cpa.minTotalWager")} {formatFiatCurrency(minWagerCents)}
              </label>
              <input
                className="input input-bordered w-full"
                value={minWagerCents}
                onChange={(e) => setMinWagerCents(e.target.value)}
                inputMode="numeric"
                placeholder="e.g. 10000 (= $100.00)"
                disabled={saving}
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button type="button" className={clsx("btn btn-ghost", { "btn-disabled": saving })} onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>

            <button type="button" className={clsx("btn btn-primary", { "btn-disabled": !canSave })} onClick={save} disabled={!canSave}>
              {saving ? <span className="loading loading-spinner loading-xs" /> : null}
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
